'use strict';
import classNames from 'classnames';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import noop from 'lodash/noop';
import * as T from 'prop-types';
import React, {Component} from 'react';
import {Subject} from 'rxjs/Subject';
import {FormField, INPUT_TYPES} from '../../../components/forms/FormField';
import {applyNumericConstraint} from '../../../core-utilities/react-form-utils';
import {I18nComponent} from '../../../core-utilities/react-i18n';


// TODO: requires adding custom logic to handle the comma separated input of the lat, long pair.
// TODO: may warrant adding a type of FormField to cover this. Definitely need to use onBlur validation and type conversion here.
export class BaseAssetFormView extends Component {
  static propTypes = {
    className: T.string,
    /** @type {String} fieldClass The class applied to each field */
    fieldClass: T.string,

    // values
    model: T.object,
    internalModel: T.object,

    canEdit: T.object,
    constraints: T.object,

    // Events
    onChange: T.object,
    onSave: T.func,

    // Form State
    isEditing: T.bool,
    isSaving: T.bool,

    nameLabel: T.string,
    identifierLabel: T.string,
    addressLabel: T.string,
    latitudeLabel: T.string,
    longitudeLabel: T.string,
    latitudePlaceholder: T.string,
    longitudePlaceholder: T.string,
  };

  static defaultProps = {
    className: '',
  };

  constructor(props) {
    super(props);

    this.handleValidateName = this.handleValidateName.bind(this);
    this.handleValidateIdentifier = this.handleValidateIdentifier.bind(this);
    this.handleValidateLatitude = this.handleValidateLatitude.bind(this);
    this.handleValidateLongitude = this.handleValidateLongitude.bind(this);
    this.handleValidationChangeLatitude = this.handleValidationChangeLatitude.bind(this);
    this.handleValidationChangeLongitude = this.handleValidationChangeLongitude.bind(this);

    this.handleChangeLatitude = this.handleChangeLatitude.bind(this);
    this.handleChangeLongitude = this.handleChangeLongitude.bind(this);

    this.triggerValidateLatitude$ = new Subject();
    this.triggerValidateLongitude$ = new Subject();
  }

  componentWillUnmount() {
    this.triggerValidateLatitude$.complete();
    this.triggerValidateLongitude$.complete();
    this.triggerValidateLatitude$ = null;
    this.triggerValidateLongitude$ = null;
  }

  handleValidationChangeLatitude() {
    this.triggerValidateLongitude$.next(null);
  }

  handleValidationChangeLongitude() {
    this.triggerValidateLatitude$.next(null);
  }

  handleChangeLatitude(...args) {
    const handleChangeLatitude = this.props.onChange.latitude || noop;
    handleChangeLatitude(...args);
    this.triggerValidateLongitude$.next(null);
  }

  handleChangeLongitude(...args) {
    const handleChangeLongitude = this.props.onChange.longitude || noop;
    handleChangeLongitude(...args);
    this.triggerValidateLatitude$.next(null);
  }

  /**
   * @param prev
   * @param next
   * @return {?{value: string, error: ?String}}
   * @private
   */
  handleValidateName({prev, next}) {
    return this.handleValidateBlankString(next, get(this.props, 'internalModel.identifier'));
  }

  /**
   * @param prev
   * @param next
   * @return {?{value: string, error: ?String}}
   * @private
   */
  handleValidateIdentifier({prev, next}) {
    return this.handleValidateBlankString(next, get(this.props, 'internalModel.name'));
  }

  /**
   * @param next
   * @param other
   * @return {?{value: string, error: ?String}}
   * @private
   */
  handleValidateBlankString(next, other) {
    if ((!isString(other) || other.length === 0) && (!isString(next) && next.length === 0)) {
      return {
        value: next,
        error: 'assets.validation.idname.blank.message',
      };
    } else {
      return null;
    }
  }

  /**
   * @param prev
   * @param next
   * @return {?{value: number, error: ?String}}
   * @private
   */
  handleValidateLatitude({prev, next}) {
    const other = get(this.props, 'internalModel.longitude');
    const constraints = this.props.constraints;
    return this.handleValidateBlankNumber(next, other, constraints);
  }

  /**
   * @param prev
   * @param next
   * @return {?{value: number, error: ?String}}
   * @private
   */
  handleValidateLongitude({prev, next}) {
    const other = get(this.props, 'internalModel.latitude');
    const constraints = this.props.constraints;
    return this.handleValidateBlankNumber(next, other, constraints);
  }

  /**
   * @param next
   * @param other
   * @param constraints
   * @return {?{value: number, error: ?String}}
   * @private
   */
  handleValidateBlankNumber(next, other, constraints) {
    if (isNumber(other) !== isNumber(next)) {
      return {
        value: applyNumericConstraint(next, constraints),
        error: {code: 'assets.validation.coords.match.message'},
      };
    } else {
      return null;
    }
  }

  render() {
    const props = this.props;
    const canEdit = props.canEdit || {};
    const constraints = props.constraints || {};
    const internalModel = props.internalModel || {};
    const onChange = props.onChange || {};

    const handleChangeName = onChange.name || noop;
    const handleChangeIdentifier = onChange.identifier || noop;
    const handleChangeAddress = onChange.address || noop;

    let tempGeo = null;
    if (props.model !== null) {
      tempGeo = props.model.geo ? JSON.parse(props.model.geo) : null;
    }

    let tempAdd = null;
    if (tempGeo !== null) {
      tempAdd = tempGeo.properties ? tempGeo.properties.address : {};
    }

    return (
      <form
        className={classNames("AssetForm", props.className)}
        onSubmit={props.onSave}
      >
        <input
          type="submit"
          hidden={true}
        />
        <FormField
          className={classNames("AssetForm__field", props.fieldClass)}
          type={INPUT_TYPES.text}
          label={props.nameLabel}
          value={internalModel.name}
          disabled={!canEdit.name}
          validateImmediately={props.validateImmediately}
          isEditing={props.isEditing}
          isSaving={props.isSaving}
          constraints={constraints.name}
          onChange={handleChangeName}
          onValidate={this.handleValidateName}
          isErrorAfterLabel={true}
        />
        <FormField
          className={classNames("AssetForm__field", props.fieldClass)}
          type={INPUT_TYPES.text}
          label={props.identifierLabel}
          value={internalModel.identifier}
          validateImmediately={props.validateImmediately}
          isEditing={props.isEditing}
          isSaving={props.isSaving}
          constraints={constraints.identifier}
          onChange={handleChangeIdentifier}
          onValidate={this.handleValidateIdentifier}
          isErrorAfterLabel={true}
        />
        <FormField
          className={classNames("AssetForm__field", props.fieldClass)}
          type={INPUT_TYPES.text}
          label={props.addressLabel}
          value={tempAdd}
          validateImmediately={props.validateImmediately}
          isEditing={props.isEditing}
          isSaving={props.isSaving}
          constraints={constraints.address}
          onChange={handleChangeAddress}
          isErrorAfterLabel={true}
        />
      </form>
    );
  }
}

export const AssetFormView = I18nComponent(BaseAssetFormView, [
  {code: 'assets.properties.name.label', prop: 'nameLabel'},
  {code: 'assets.properties.identifier.label', prop: 'identifierLabel'},
  {code: 'assets.properties.address.label', prop: 'addressLabel'},
  {code: 'assets.properties.latitude.label', prop: 'latitudeLabel'},
  {code: 'assets.properties.longitude.label', prop: 'longitudeLabel'},
  {code: 'assets.properties.latitude.placeholder', prop: 'latitudePlaceholder'},
  {code: 'assets.properties.longitude.placeholder', prop: 'longitudePlaceholder'},
]);



// WEBPACK FOOTER //
// ./src/js/assets/components/AssetForm/AssetFormView.js